import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslatedVariables } from 'src/app/commons/translated-variables';
import { CoursesService } from 'src/app/core/services/courses-service';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { CourseFilters, CourseLanguages, CourseProgram, CourseUserLanguage, LearningAreasString } from 'src/app/model/course-program';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { UniversityService } from 'src/app/core/services/university.service';
import { LearningArea, ProductLearningArea } from 'src/app/model/ProductLearningArea';
import { NavigateService } from 'src/app/core/services/navigate.service';
import { LoginService } from 'src/app/core/services/login.service';
import { TbAuthService } from '@tb/auth';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ms-courses-viewer',
  templateUrl: './courses-viewer.component.html',
  styleUrls: ['./courses-viewer.component.scss'],
  animations: [
    trigger('detailExpand', [
        state('collapsed', style({ height: '0px', minHeight: '0' })),
        state('expanded', style({ height: '*' })),
        transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class CoursesViewerComponent implements OnInit {

  constructor(
    private variable: TranslatedVariables,
    private courseService: CoursesService,
    private router: Router,
    private _formBuilder: FormBuilder,
    private navigateService: NavigateService,
    private loginService: LoginService,
    private tbAuthService: TbAuthService,
    public universityService: UniversityService,
    private translate: TranslateService
  ) { }

  search = this._formBuilder.group({
    searchString: '',
    courseArea: '',
    courseProduct: '',
    courseLanguage: ''
  });

  @Input() trainingPlans!: boolean;

  expandedElement: CourseProgram | null;
  courseProgramsTable: MatTableDataSource<CourseProgram> = new MatTableDataSource<CourseProgram>();
  originalCoursePrograms: CourseProgram[] = [];
  displayedColumns: string[] = ['product'];
  isLoading: boolean = false;

  courseImageLoading: string[] = [];
  translatedLanguages: { [id: string]: string };

  areasString: LearningArea[] = [];
  productLearningAreas: ProductLearningArea[] = [];
  lastProductCode: string = "";
  lastSearch: string = "";

  _coursespaginator: MatPaginator;
  @ViewChild('coursesPaginator') set paginator(contentOrders: MatPaginator) {
    if (contentOrders) {
      this._coursespaginator = contentOrders;
      const paginatorIntl = this._coursespaginator._intl;
      paginatorIntl.nextPageLabel = this.variable.nextPage;
      paginatorIntl.previousPageLabel = this.variable.previousPage;
      if (this.courseProgramsTable) {
        this.courseProgramsTable.paginator = this._coursespaginator;
      }
    }
  }



  async ngOnInit() {
    this.isLoading = true;
    try {
      let res = await this.translate.get([
        "LANGUAGES.ENGLISH",
        "LANGUAGES.ITALIAN",
        "LANGUAGES.ROMANIAN",
        "LANGUAGES.GERMAN"
      ]).toPromise();

      this.translatedLanguages = {
        "en-EN": res["LANGUAGES.ENGLISH"],
        "en-US": res["LANGUAGES.ENGLISH"],
        "it-IT": res["LANGUAGES.ITALIAN"],
        "ro-RO": res["LANGUAGES.ROMANIAN"],
        "de-DE": res["LANGUAGES.GERMAN"]
      }

      var coursesRes = await this.courseService.getAllCourses(this.trainingPlans)
      if(coursesRes.Result && coursesRes.Content){
        this.courseProgramsTable.data = coursesRes.Content
        this.originalCoursePrograms = this.courseProgramsTable.data
        coursesRes.Content.forEach(async (cp) => {
          cp.CourseImage = await this.courseImage(cp)
        })
      }

      var learningAreasRes = await this.courseService.getAllProductLearningAreas()
      if(!learningAreasRes.Result || !learningAreasRes.Content) throw Error('Learning areas not found')
      this.productLearningAreas = learningAreasRes.Content;

      let opRes = await this.loginService.getAccountToPromiseBy(this.tbAuthService.getAccountName());
      if(!opRes['Content'].Language || !this.translatedLanguages[opRes['Content'].Language])
        this.search.controls['courseLanguage'].setValue('en-EN')
      else
        this.search.controls['courseLanguage'].setValue(opRes['Content'].Language)

      let filtersName: string = this.trainingPlans ? 'trainingsFilters' : 'coursesFilters'
      if(this.navigateService.getLocalStorageData<CourseFilters>(filtersName)){
        let filters: CourseFilters = this.navigateService.getLocalStorageData<CourseFilters>(filtersName)
        this.search.controls['searchString'].setValue(filters.searchString);
        this.lastSearch = filters.searchString;
        this.search.controls['courseLanguage'].setValue(filters.courseLanguage);
        this.search.controls['courseProduct'].setValue(filters.courseProduct);
        this.setVisibleCourses(this.search.value)
        this.search.controls['courseArea'].setValue(filters.courseArea);
      }


      this.setVisibleCourses(this.search.value)

      this.isLoading = false
    } catch(err) {
      this.isLoading = false
    }

    this.search.valueChanges.debounceTime(250).subscribe(value => { this.setVisibleCourses(value) })
  }

  getTitleString(): string{
    if(this.trainingPlans) return 'COMMONS.PLANLIST'
     return 'COMMONS.COURSELIST'
  }
  getSearchString(): string{
    if(this.trainingPlans) return 'COMMONS.COURSEPLANSEARCH'
     return 'COMMONS.COURSESEARCH'
  }

  setVisibleCourses(parametes: object){
    if(parametes['searchString'] != this.lastSearch && parametes['searchString']){
      this.search.controls['courseArea'].setValue('');
      this.search.controls['courseLanguage'].setValue('');
      this.search.controls['courseProduct'].setValue('');
    }

    this.courseProgramsTable.data = this.originalCoursePrograms.filter(cp => {
      return (cp.Description.toLowerCase().trim().includes(parametes['searchString']?.toLowerCase().trim() ?? '') || cp.Program.toLowerCase().trim().includes(parametes['searchString']?.toLowerCase().trim() ?? '') ) &&
      (cp.ProductCode == parametes['courseProduct'] || parametes['courseProduct'] == '' ) &&
      (cp.LearningArea == parametes['courseArea'] || parametes['courseArea'] == '' ) &&
      (CourseLanguages[cp.CourseLanguage]?.includes(CourseUserLanguage[parametes['courseLanguage']] ?? '') || parametes['courseLanguage'] == '' )
    })

    if(this.lastProductCode != parametes['courseProduct']){
      this.areasString = [];
      this.lastProductCode = parametes['courseProduct'];
      if(parametes['courseProduct']){
        this.search.controls['courseArea'].setValue('');
        this.areasString = this.productLearningAreas.find(
          pla => pla.ProductCode == parametes['courseProduct']
        ).LearningAreas
      }
    }
    this.navigateService.setLocalStorageData(this.trainingPlans ? 'trainingsFilters' : 'coursesFilters', JSON.stringify(parametes))
    this.lastSearch = parametes['searchString'];
  }

  getAreaString(areaCode: number): string {
    return LearningAreasString[`AREA${areaCode.toString()}`]
  }

  getCourseLanguages(): string[] {
    let languages: string[] = [];
    Object.values(this.translatedLanguages).forEach(value => {
      if(!languages.includes(value))
        languages.push(value)
    });
    return languages.sort()
  }

  getLanguageKey(language: string){
    let keyRes: string
    Object.keys(this.translatedLanguages).forEach(key => {
      if(this.translatedLanguages[key] == language)
        keyRes = key
    });
    return keyRes;
  }


  clearSearch(){
    this.search.controls['searchString'].setValue('');
  }

  async courseImage(course: CourseProgram): Promise<string>{
    this.courseImageLoading.push(course.CourseName);
    let url = await this.universityService.getImage(course)
    this.courseImageLoading = this.courseImageLoading.filter(ci => { ci != course.CourseName })
    return url;
  }

  showCourseDetail(selectedCourse: CourseProgram){
    this.navigateService.setLocalStorageData('selectedCourse', JSON.stringify(selectedCourse))
    this.navigateService.setLocalStorageData('trainingPlans', JSON.stringify(selectedCourse.TrainingPlan))
    this.router.navigate(['/mago-university/mago-courses/course-detail']);
  }

}
